import {DodPreset, DodPresetMap, RecommendedSelections, RecommendedSelectionsMap} from "@/types/ApiTypes";
import {ApiClient, defaultPublicApiClient, defaultTenantApiClient} from "@/api/ApiClient";
import {DodRunConfig, DodSchedule, FactCondition} from "@/types/DodRun";
import { defaultPpgName, defaultPpgNameForDod } from "@/config/globalVars";

type ExtractWizardStep = 1 | 2 | 3 | 4 | 5;

/**
 * Returns a validated wizard step from layout config.
 *
 * Why this exists:
 * - UI wizard may provide an explicit step marker in `layout.extractWizardStep`.
 * - Older flows (including historical scheduled runs) may not provide it.
 *
 * Behavior:
 * - Returns the step only when it is a supported numeric value (1..5).
 * - Returns `undefined` when missing/invalid so payload generation can omit it.
 */
function getExtractWizardStep(config: DodRunConfig): ExtractWizardStep | undefined {
    const step = config?.layout?.extractWizardStep;
    if (step === 1 || step === 2 || step === 3 || step === 4 || step === 5) {
        return step;
    }
    return undefined;
}

/**
 * Conditionally injects `layout.extractWizardStep` into normalized config.
 *
 * Important:
 * - We intentionally do not force a default/hardcoded step value.
 * - If the caller does not provide a valid step, payload is left unchanged.
 * - This keeps backward compatibility for old data-mode/scheduled flows.
 */
function withExtractWizardStep(normalizedConfig: any, extractWizardStep?: ExtractWizardStep) {
    if (extractWizardStep === undefined) {
        return normalizedConfig;
    }

    return {
        ...normalizedConfig,
        layout: {
            ...normalizedConfig.layout,
            extractWizardStep,
        },
    };
}

export async function getDodPresets(client: ApiClient = defaultTenantApiClient): Promise<DodPresetMap> {
    return client.httpGet(`/my/extract_presets`);
}

// export async function getRecommendedSelections(client: ApiClient = defaultTenantApiClient): Promise<RecommendedSelectionsMap> {
//     return client.httpGet(`/my/dod_recommended_selections`);
// }

export async function updateDodPreset(id: number, values: any, client: ApiClient = defaultTenantApiClient): Promise<DodPreset> {
    return client.httpPatch(`/my/extract_presets/${id}`, values);
}

export async function createDodPreset(preset: Omit<DodPreset, 'id'>, client: ApiClient = defaultTenantApiClient): Promise<DodPreset> {
    return client.httpPost('/my/extract_presets', preset);
}

export async function createRecommendedSelections(selections: Omit<RecommendedSelections, 'id'>, client: ApiClient = defaultTenantApiClient): Promise<RecommendedSelections> {
    return client.httpPost('/my/dod_recommended_selections', selections);
}

export async function getUniversalRecommendedSelection(id: number, client: ApiClient = defaultTenantApiClient): Promise<RecommendedSelections> {
    return client.httpGet(`/my/dod_recommended_selections/${id}`);
}

export async function deleteUniversalRecommendedSelection(id: number, client: ApiClient = defaultTenantApiClient): Promise<void> {
    return client.httpDelete(`/my/dod_recommended_selections/${id}`);
}

export async function deleteDodPreset(id: number, client: ApiClient = defaultTenantApiClient): Promise<void> {
    return client.httpDelete(`/my/extract_presets/${id}`);
}

export async function getDodPreset(id: number, client: ApiClient = defaultTenantApiClient): Promise<DodPreset> {
    return client.httpGet(`/my/extract_presets/${id}`);
}

export async function getDodExcelTemplates(client: ApiClient = defaultTenantApiClient) {
    return client.httpGet(`/extract_templates`);
}

let dataPointsAbortController: AbortController;
export async function estimateExtractDataPoints(config: DodRunConfig, sessionId: string, client: ApiClient = defaultTenantApiClient): Promise<{estimate: number; approximate?: boolean}> {

    // cancel any previous active request before sending the next one.
    dataPointsAbortController?.abort();
    dataPointsAbortController = new AbortController();

    const normalizedConfig = normalizeDodConfig(config);
    // Counter mode estimate can run at multiple wizard stages, including layout/conditions.
    // We forward step only when explicitly present in the incoming config.
    const stepTaggedConfig = withExtractWizardStep(normalizedConfig, getExtractWizardStep(config));

    const {estimate, approximate} = await client.httpPost('/extracts.estimate_data_points', {...stepTaggedConfig, sessionId}, {
        signal: dataPointsAbortController.signal
    });
    return {estimate, approximate};
}

export async function createExtract(params : {config: DodRunConfig, sessionId: string, isFutureTemplate?: boolean, client?: ApiClient}): Promise<DodRunConfig> {
    const { config, sessionId, isFutureTemplate = false, client = defaultTenantApiClient } = params;
    const normalizedConfig = normalizeDodConfig(config);
    // Data mode: keep extractWizardStep optional for backward compatibility.
    // If the wizard passed a step marker we include it; otherwise we omit it.
    const stepTaggedConfig = withExtractWizardStep(normalizedConfig, getExtractWizardStep(config));

    return client.httpPost('/extracts', {
        ...stepTaggedConfig,
        sessionId,
        isFutureTemplate
    });
}

export async function runExtractLayoutChange( extractId: number, config: DodRunConfig, isFutureTemplate: boolean = false, client: ApiClient = defaultTenantApiClient ) {
    const normalizedConfig = normalizeDodConfig(config);
    // Apply-layout run is also data mode; do not hardcode step values.
    const stepTaggedConfig = withExtractWizardStep(normalizedConfig, getExtractWizardStep(config));

    return client.httpPost(`/data_on_demand/create_extract_hack_layout`, {
        extractId,
        runConfig: {
            ...stepTaggedConfig,
            isFutureTemplate
        },
    });
}

export async function createExtractSchedule(config: DodRunConfig, schedule?: DodSchedule, isFutureTemplate: boolean = false, client: ApiClient = defaultTenantApiClient): Promise<DodRunConfig> {

    return client.httpPost('/extract_schedules', {
        ...normalizeDodConfig(config),
        isFutureTemplate,
        schedule,
    });
}

export async function updateExtractSchedule(id: number, config: DodRunConfig, schedule?: DodSchedule, isFutureTemplate: boolean = false, client: ApiClient = defaultTenantApiClient): Promise<DodRunConfig> {

  return client.httpPut('/extract_schedules', {
      ...normalizeDodConfig(config),
      isFutureTemplate,
      schedule,
      id
  });
}

export async function getDodSeriesNames(
    client: ApiClient = defaultTenantApiClient
): Promise<{ [key: string]: string[] }> {
    return client.httpGet('/extracts/series_names');
}

export async function updateExtractName(id: number, name: string, type: 'name' | 'series' = "name", isSchedule = false, client: ApiClient = defaultTenantApiClient): Promise<any> {
    if(isSchedule){
        return client.httpPatch(`/extracts_schedule_series_name/${id}`, {id, name});
    }
    return client.httpPatch(`/extracts_name/${id}/${type}`, {
        name
    });
}

export function normalizeDodConfig(config: DodRunConfig): any {
    // Handle legacy FactCondition[] shape as well as the new DodConditions object shape
    const legacyConditions = Array.isArray(config.conditions);
    const conditions = legacyConditions
        ? { facts: config.conditions as unknown as FactCondition[], market: 'each_market', timePeriod: 'each_time_period' }
        : config.conditions;

    const market = conditions?.market || 'each_market';
    const timePeriod = conditions?.timePeriod || 'each_time_period';
    const products = (conditions as any)?.products ?? (conditions as any)?.productFilter ?? { mode: 'All' };

    return {
        ...config,
        conditions: conditions ? {
            market,
            timePeriod,
            name: conditions.name,
            facts: conditions.facts ?? [],
            products,
        } : { facts: [], market: 'each_market', timePeriod: 'each_time_period', products: { mode: 'All' } },
        filters: {
            ...config.filters,
            markets: {
                // convert market displays names to keys
                values: config.filters.markets.values.map((v) => v.key),
                summedSelections: config.filters.markets.summedSelections.map((v) => ({
                    name: v.name,
                    values: v.values.map((v) => v.key),
                })),
            },
            characteristics: Object.values(config.filters.characteristics),
            customCharacteristics: Object.values(config.filters.customCharacteristics),
            marketCharacteristics: Object.values(config.filters.marketCharacteristics ?? {}).map((mc) => ({
                ...mc,
                summedSelections: [],
            })),
            ppgs: Object.values(config.filters.ppgs).map((ppg) => ({
                ...ppg,
                displayName: ppg.displayName === defaultPpgName ? defaultPpgNameForDod : ppg.displayName,
            })),
        },
        layout: {
            ...config.layout,
            rows: config.layout.rows.map((row) => ({
                ...row,
                dim: row.dim === defaultPpgName ? defaultPpgNameForDod : row.dim,
            })),
            columns: config.layout.columns.map((col) => ({
                ...col,
                dim: col.dim === defaultPpgName ? defaultPpgNameForDod : col.dim,
            })),
        },
    };
}
